import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAltaUsuari,
  useCercaEntra,
  useRols,
  type PersonaEntraDto,
  type RolDto,
} from '@/api/administracio';
import type { Ambit } from '@/api/types';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Avatar } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { Calaix } from '../marc/Calaix';
import { CercaAmbit } from '../marc/CercaAmbit';
import type { NodePla } from '../marc/estructura';
import { ErrorDesa } from '../comu';

function useRetardat(valor: string, ms = 300) {
  const [retardat, setRetardat] = useState(valor);
  useEffect(() => {
    const id = window.setTimeout(() => setRetardat(valor), ms);
    return () => window.clearTimeout(id);
  }, [valor, ms]);
  return retardat;
}

function Bloc({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="camp">
      <span className="camp__label">{etiqueta}</span>
      {children}
    </div>
  );
}

const nomPersona = (p: PersonaEntraDto) =>
  `${p.nom ?? ''} ${p.cognoms ?? ''}`.trim() || p.upn || p.email || p.entraOid;

export function AltaPersona({
  onTanca,
  onCreat,
}: {
  onTanca: () => void;
  onCreat?: (usuariId: number) => void;
}) {
  const { t, i18n } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const es = i18n.language === 'es';
  const { me } = useMe();
  const rols = useRols();
  const alta = useAltaUsuari();
  const [text, setText] = useState('');
  const cerca = useCercaEntra(useRetardat(text));
  const [persona, setPersona] = useState<PersonaEntraDto | null>(null);
  const [rolId, setRolId] = useState<number | null>(null);
  const [global, setGlobal] = useState(false);
  const [lloc, setLloc] = useState<NodePla | null>(null);
  const potGlobal = !!me?.permisos.some(
    (p) => p.recurs === 'cai.usuaris' && p.tipusAcces === 'ALTA' && p.ambit === 'GLOBAL',
  );

  const nomRol = (r: RolDto) => (es ? r.nomEs : r.nomCa) ?? r.codi;
  const ambit: Ambit | null = global ? 'GLOBAL' : (lloc?.tipus ?? null);
  const llest = !!persona && rolId != null && ambit != null;

  const envia = () => {
    if (!persona || rolId == null || ambit == null) return;
    alta.mutate(
      { entraOid: persona.entraOid, rolId, ambit, ambitId: global ? null : (lloc?.id ?? null) },
      {
        onSuccess: (u) => {
          if (u?.usuari?.id != null) onCreat?.(u.usuari.id);
          onTanca();
        },
      },
    );
  };

  return (
    <Calaix
      titol={t('altaPersona.titol')}
      etiqueta={t('altaPersona.etiqueta')}
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('altaPersona.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--s btn--dark"
            disabled={!llest || alta.isPending}
            onClick={envia}
          >
            {t('altaPersona.crea')}
          </button>
        </>
      }
    >
      <p className="t-footnote dim">{t('altaPersona.ajuda')}</p>
      {persona ? (
        <Bloc etiqueta={t('altaPersona.cerca')}>
          <div className="hstack">
            <Avatar nom={nomPersona(persona)} />
            <span className="stack" style={{ flex: 1, gap: 0 }}>
              <span className="strong">{nomPersona(persona)}</span>
              <span className="t-caption dim">
                {[persona.carrec, persona.departament, persona.upn ?? persona.email]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            {persona.usuariId != null ? (
              <span className="badge badge--preventiu">{t('altaPersona.jaAlCai')}</span>
            ) : null}
            <button type="button" className="btn btn--ghost btn--xs" onClick={() => setPersona(null)}>
              {t('altaPersona.canvia')}
            </button>
          </div>
        </Bloc>
      ) : (
        <Bloc etiqueta={t('altaPersona.cerca')}>
          <input
            className="input"
            type="search"
            value={text}
            placeholder={t('altaPersona.cercaPlaceholder')}
            onChange={(e) => setText(e.target.value)}
          />
          {text.trim().length >= 2 ? (
            <div className="stack" role="listbox" aria-label={t('altaPersona.cerca')}>
              {cerca.isFetching && !cerca.data ? (
                <span className="t-caption dim">{t('altaPersona.cercant')}</span>
              ) : cerca.isError ? (
                <ErrorDesa error={cerca.error} />
              ) : (cerca.data ?? []).length === 0 ? (
                <span className="t-caption dim">{t('altaPersona.senseResultats')}</span>
              ) : (
                (cerca.data ?? []).map((p) => (
                  <button
                    key={p.entraOid}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="menu__item"
                    onClick={() => setPersona(p)}
                  >
                    <Avatar nom={nomPersona(p)} />
                    <span className="stack" style={{ flex: 1, gap: 0, textAlign: 'left' }}>
                      <span className="strong">{nomPersona(p)}</span>
                      <span className="t-caption dim">
                        {[p.carrec, p.departament, p.upn ?? p.email].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    {p.usuariId != null ? (
                      <span className="badge badge--preventiu">{t('altaPersona.jaAlCai')}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          ) : null}
        </Bloc>
      )}
      <Bloc etiqueta={t('altaPersona.rol')}>
        <Desplegable
          aria-label={t('altaPersona.rol')}
          placeholder={t('altaPersona.rolPlaceholder')}
          valor={rolId ?? ''}
          opcions={(rols.data ?? [])
            .filter((r) => r.id != null)
            .map((r) => ({
              valor: r.id as number,
              etiqueta: nomRol(r),
              detall: r.descripcio ?? undefined,
            }))}
          onCanvia={(v) => setRolId(Number(v))}
        />
      </Bloc>
      <Bloc etiqueta={t('altaPersona.on')}>
        {global ? (
          <div className="hstack">
            <span className="xip-ambit xip-ambit--global">{t('altaPersona.global')}</span>
            <button type="button" className="btn btn--ghost btn--xs" onClick={() => setGlobal(false)}>
              {t('altaPersona.canvia')}
            </button>
          </div>
        ) : lloc ? (
          <div className="hstack">
            <span className="xip-ambit" title={tc(`ambit.${lloc.tipus}`)}>
              <span
                className={`node-punt node-punt--${lloc.tipus.toLowerCase()}`}
                aria-hidden="true"
              />
              {lloc.ruta ? `${lloc.ruta} › ${lloc.nom}` : lloc.nom}
            </span>
            <button type="button" className="btn btn--ghost btn--xs" onClick={() => setLloc(null)}>
              {t('altaPersona.canvia')}
            </button>
          </div>
        ) : (
          <div className="hstack">
            <CercaAmbit
              aria-label={t('altaPersona.triaAmbit')}
              placeholder={t('altaPersona.triaAmbit')}
              onTria={(n) => setLloc(n)}
            />
            {potGlobal ? (
              <button type="button" className="btn btn--ghost btn--xs" onClick={() => setGlobal(true)}>
                <Icona nom="globe" />
                {t('altaPersona.global')}
              </button>
            ) : null}
          </div>
        )}
      </Bloc>
      <ErrorDesa error={alta.error} />
    </Calaix>
  );
}
